import { api } from '@/lib/api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  BulkActivateCanvassersParams,
  BulkAssignToJobParams,
  BulkDeactivateCanvassersParams,
  BulkDeleteCanvassersParams,
  BulkUnassignFromJobParams,
  Canvasser,
  CanvasserDetails,
  CanvasserFilter,
  CanvasserSort,
  generatePlaceholderStats,
  ResetPasswordParams,
  SortDirection,
} from './types';

interface CanvasserFilterParams {
  filter: CanvasserFilter;
  search?: string;
  sort: CanvasserSort;
  sortDirection: SortDirection;
}

// Fetch canvassers (reuses existing agents endpoint)
const _fetchCanvassers = async (
  accountId: string
): Promise<AxiosResponse<Canvasser[]>> => {
  return api.get(`1to1/agents?account_id=${accountId}`);
};

export const useFetchCanvassers = (
  accountId?: string,
  enabled?: boolean,
  filterParams?: CanvasserFilterParams
) => {
  return useQuery({
    queryKey: ['fetch-canvassers', { accountId, ...filterParams }],
    queryFn: () => _fetchCanvassers(accountId!),
    enabled: !!accountId && (!!enabled || typeof enabled === 'undefined'),
    retry: 0,
  });
};

// Fetch canvasser details
const _fetchCanvasserDetails = async (
  canvasserId: string
): Promise<AxiosResponse<Canvasser>> => {
  return api.get(`v2/p2p/agents/${canvasserId}`);
};

export const useFetchCanvasserDetails = (canvasserId?: string, enabled?: boolean) => {
  return useQuery({
    queryKey: ['fetch-canvasser-details', { canvasserId }],
    queryFn: canvasserId
      ? async () => {
          const response = await _fetchCanvasserDetails(canvasserId);
          // Add placeholder data for conversation stats and current job
          const placeholderData = generatePlaceholderStats(canvasserId);
          return {
            ...response,
            data: {
              ...response.data,
              ...placeholderData,
            } as CanvasserDetails,
          };
        }
      : undefined,
    enabled: !!canvasserId && (!!enabled || typeof enabled === 'undefined'),
    retry: 0,
  });
};

// Reset canvasser password
const _resetCanvasserPassword = async ({
  canvasser_id,
  password,
}: ResetPasswordParams): Promise<AxiosResponse<Canvasser>> => {
  return api.post(`v2/p2p/agents/${canvasser_id}/resetpassword`, { password });
};

export const useResetCanvasserPassword = () => {
  return useMutation({
    mutationFn: _resetCanvasserPassword,
  });
};

// Bulk operations (placeholders for future API implementation)
// These will need to be implemented when the bulk operation APIs are available

export const useBulkActivateCanvassers = () => {
  return useMutation({
    mutationFn: async (params: BulkActivateCanvassersParams) => {
      // TODO: Implement when API is available
      // return api.post('v2/p2p/agents/bulk/activate', params);
      console.log('Bulk activate canvassers:', params);
      return Promise.resolve({ data: { success: true } });
    },
  });
};

export const useBulkDeactivateCanvassers = () => {
  return useMutation({
    mutationFn: async (params: BulkDeactivateCanvassersParams) => {
      // TODO: Implement when API is available
      // return api.post('v2/p2p/agents/bulk/deactivate', params);
      console.log('Bulk deactivate canvassers:', params);
      return Promise.resolve({ data: { success: true } });
    },
  });
};

export const useBulkDeleteCanvassers = () => {
  return useMutation({
    mutationFn: async (params: BulkDeleteCanvassersParams) => {
      // TODO: Implement when API is available
      // return api.delete('v2/p2p/agents/bulk/delete', { data: params });
      console.log('Bulk delete canvassers:', params);
      return Promise.resolve({ data: { success: true } });
    },
  });
};

export const useBulkAssignToJob = () => {
  return useMutation({
    mutationFn: async (params: BulkAssignToJobParams) => {
      // TODO: Use existing job assignment endpoints
      console.log('Bulk assign to job:', params);
      return Promise.resolve({ data: { success: true } });
    },
  });
};

export const useBulkUnassignFromJob = () => {
  return useMutation({
    mutationFn: async (params: BulkUnassignFromJobParams) => {
      // TODO: Use existing job assignment endpoints
      console.log('Bulk unassign from job:', params);
      return Promise.resolve({ data: { success: true } });
    },
  });
};
